import React from "react";
import {
  Award,
  Leaf,
  HeartPulse,
  ShieldCheck,
  ArrowUp,
  GraduationCap,
} from "lucide-react";

export const ACCREDITATIONS = [
  {
    icon: Award,
    code: "ISO 9001",
    title: "Quality Management",
    body: "Certified processes for consistent, high-quality engineering delivery.",
  },
  {
    icon: Leaf,
    code: "ISO 14001",
    title: "Environmental Management",
    body: "Audited environmental controls across every site and project.",
  },
  {
    icon: HeartPulse,
    code: "ISO 45001",
    title: "Occupational H&S",
    body: "Industry-leading health & safety management for our team and yours.",
  },
  {
    icon: ShieldCheck,
    code: "SafeContractor",
    title: "Approved Contractor",
    body: "Independently vetted contractor competence and compliance.",
  },
  {
    icon: ArrowUp,
    code: "IPAF",
    title: "Powered Access Trained",
    body: "IPAF-certified operators for safe, competent use of mobile elevating work platforms at height.",
  },
  {
    icon: GraduationCap,
    code: "City & Guilds",
    title: "Qualified Engineers",
    body: "Recognised City & Guilds qualifications behind our engineering workforce.",
  },
];

const BADGES = [
  {
    src: "/brand/accreditations/iso-ims.png",
    alt: "ISO 9001, ISO 14001 and ISO 45001 Integrated Management System certification badge",
    testid: "badge-iso-ims",
    fit: "tall",
  },
  {
    src: "/brand/accreditations/safecontractor.png",
    alt: "SafeContractor approved certification seal",
    testid: "badge-safecontractor",
    fit: "square",
  },
  {
    src: "/brand/accreditations/ipaf.png",
    alt: "IPAF (International Powered Access Federation) certification logo",
    testid: "badge-ipaf",
    fit: "wide",
  },
  {
    src: "/brand/accreditations/cityguilds.png",
    alt: "City & Guilds qualification logo",
    testid: "badge-cityguilds",
    fit: "square",
  },
];

const BadgeCard = ({ src, alt, testid, fit }) => {
  const maxH = fit === "tall" ? 240 : fit === "wide" ? 110 : 180;
  return (
    <div
      className="flex items-center justify-center rounded-2xl p-6 lg:p-8"
      style={{
        background: "linear-gradient(180deg, #ffffff 0%, #e9eef9 100%)",
        border: "1px solid rgba(127, 166, 255, 0.35)",
        minHeight: 240,
        boxShadow: "0 18px 50px -22px rgba(47, 107, 255, 0.45)",
      }}
      data-testid={testid}
    >
      <img
        src={src}
        alt={alt}
        className="block w-auto"
        style={{ objectFit: "contain", maxHeight: maxH, maxWidth: "100%" }}
        loading="lazy"
      />
    </div>
  );
};

const Accreditations = () => {
  return (
    <section
      className="relative mt-24 lg:mt-32"
      id="accreditations"
      data-testid="accreditations-section"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="eyebrow">Accreditations &amp; Compliance</p>
            <h2
              className="font-display mt-5 text-white"
              style={{ fontSize: "clamp(36px, 5vw, 60px)", lineHeight: 0.95 }}
            >
              INDEPENDENTLY
              <br />
              <span className="gradient-blue-text">CERTIFIED &amp; AUDITED.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
              Trusted by industrial operators because our work is held to externally
              audited standards — quality, environment, health &amp; safety, electrical
              competence, powered access and qualified engineering.
            </p>
          </div>
        </div>

        {/* Official certification badges */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6"
          data-testid="accreditation-badges"
        >
          {BADGES.map((b) => (
            <BadgeCard key={b.testid} {...b} />
          ))}
        </div>

        {/* Descriptive cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {ACCREDITATIONS.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.code}
                className="service-card rounded-xl p-5 lg:p-6"
                data-testid={`accreditation-${a.code.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="icon-tile flex-shrink-0"
                    style={{ width: 44, height: 44 }}
                  >
                    <Icon size={20} />
                  </span>
                  <div className="min-w-0">
                    <p
                      className="font-display text-white"
                      style={{
                        fontSize: 17,
                        letterSpacing: "0.04em",
                        lineHeight: 1.05,
                      }}
                    >
                      {a.code.toUpperCase()}
                    </p>
                    <p className="text-[10.5px] uppercase tracking-widest text-[color:var(--lt-blue-soft)] font-semibold mt-1">
                      {a.title}
                    </p>
                    <p className="mt-2 text-[13px] text-[color:var(--lt-text-dim)] leading-relaxed">
                      {a.body}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Accreditations;
